import {
  ActiveCoachingMethod,
  ActiveCoachingTactic,
  ActiveDurationOptions,
  ActivePreparationTimeOptions,
} from '@amzn/aet-coachingform-kit/lib/types';
import { IntlShape } from 'react-intl';

import {
  CoachingMethodObject,
  CoachingTacticObject,
  DurationOptionsObject,
  PreparationTimeOptionsObject,
} from '../../../api/CoachingForm/schema';
import { getTranslatedMoment } from '../../../translations';
import { getObjectOptions } from '../FieldComponents/Helpers';
import { Option } from '../FieldComponents/Select';

const moment = getTranslatedMoment();

export const getAllTactics = (intl: IntlShape): Option[] => {
  const translations: Record<string, string> = {
    [CoachingTacticObject.ROLEPLAY]: intl.formatMessage({
      id: 'cs_perfportal_coaching_session_options_tactic_role_playing',
      defaultMessage: 'Role playing',
    }),
    [CoachingTacticObject.LIVE]: intl.formatMessage({
      id: 'cs_perfportal_coaching_session_options_tactic_live_listen_in',
      defaultMessage: 'Live listen-in',
    }),
    [CoachingTacticObject.REFLECT]: intl.formatMessage({
      id: 'cs_perfportal_coaching_session_options_tactic_review_and_reflect',
      defaultMessage: 'Review and reflect',
    }),
    [CoachingTacticObject.TANDEM]: intl.formatMessage({
      id: 'cs_perfportal_coaching_session_options_tactic_tandem_review',
      defaultMessage: 'Tandem review',
    }),
    [CoachingTacticObject.REVIEW]: intl.formatMessage({
      id: 'cs_perfportal_coaching_session_options_tactic_review',
      defaultMessage: 'Review',
    }),
  };

  return getObjectOptions(CoachingTacticObject, translations);
};

export const getTactics = (intl: IntlShape): Option[] => {
  return getAllTactics(intl).filter(
    tactic => tactic.value in ActiveCoachingTactic
  );
};

export const getAllMethods = (intl: IntlShape): Option[] => {
  const translations: Record<string, string> = {
    [CoachingMethodObject.PERSON]: intl.formatMessage({
      id: 'cs_perfportal_coaching_session_options_method_scheduled_in_person',
      defaultMessage: 'Scheduled - in person',
    }),
    [CoachingMethodObject.MEETING]: intl.formatMessage({
      id: 'cs_perfportal_coaching_session_options_method_scheduled_chime_meeting',
      defaultMessage: 'Scheduled - Chime meeting',
    }),
    [CoachingMethodObject.CHAT]: intl.formatMessage({
      id: 'cs_perfportal_coaching_session_options_method_chime_chat',
      defaultMessage: 'Chime chat',
    }),
    [CoachingMethodObject.EMAIL]: intl.formatMessage({
      id: 'cs_perfportal_channel_email',
      defaultMessage: 'Email',
    }),
  };

  return getObjectOptions(CoachingMethodObject, translations);
};

export const getMethods = (intl: IntlShape): Option[] => {
  return getAllMethods(intl).filter(
    method => method.value in ActiveCoachingMethod
  );
};

export const getAllDurations = (): Option[] => {
  const translations: Record<string, string> = {
    [DurationOptionsObject.MINUTES15]: moment
      .duration(15, 'minutes')
      .humanize(),
    [DurationOptionsObject.MINUTES30]: moment
      .duration(30, 'minutes')
      .humanize(),
    [DurationOptionsObject.MINUTES45]: moment
      .duration(45, 'minutes')
      .humanize({ m: 59 }),
    [DurationOptionsObject.MINUTES60]: moment.duration(1, 'hours').humanize(),
  };

  return getObjectOptions(DurationOptionsObject, translations);
};

export const getDurations = (): Option[] => {
  return getAllDurations().filter(
    duration => duration.value in ActiveDurationOptions
  );
};

export const getAllPreparationTimes = (intl: IntlShape): Option[] => {
  const translations: Record<string, string> = {
    [PreparationTimeOptionsObject.UNDER5]: intl.formatMessage({
      id: 'cs_perfportal_time_range_minutes_0_to_5',
      defaultMessage: '5 minutes or less',
    }),
    [PreparationTimeOptionsObject.FROM6TO10]: intl.formatMessage({
      id: 'cs_perfportal_time_range_minutes_6_to_10',
      defaultMessage: '6-10 minutes',
    }),
    [PreparationTimeOptionsObject.FROM11TO15]: intl.formatMessage({
      id: 'cs_perfportal_time_range_minutes_11_to_15',
      defaultMessage: '11-15 minutes',
    }),
    [PreparationTimeOptionsObject.FROM16TO29]: intl.formatMessage({
      id: 'cs_perfportal_time_range_minutes_16_to_29',
      defaultMessage: '16-29 minutes',
    }),
    [PreparationTimeOptionsObject.OVER30]: intl.formatMessage({
      id: 'cs_perfportal_time_range_minutes_30_or_more',
      defaultMessage: '30 minutes or more',
    }),
  };

  return getObjectOptions(PreparationTimeOptionsObject, translations);
};

export const getPreparationTimes = (intl: IntlShape): Option[] => {
  return getAllPreparationTimes(intl).filter(
    time => time.value in ActivePreparationTimeOptions
  );
};
